import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type CompStage, type MatchSummary } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { STAGE_TYPE_LABELS } from '../components/StagePipelinePanel';
import { DivisionFixturesCarousel } from '../components/DivisionFixturesCarousel';
import { useWorkspace } from '../lib/workspace';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { clanLabelFrom } from '../lib/displayName';
import { groupMatchesByStage, divisionLetter, type StageMatchGroup } from '../lib/groupMatchesByStage';
import { Swords, ChevronRight, Trophy } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Matches ("Wall #2") — the standalone /matches board. Gives org admins a
// clean, reachable place to find and open matches for their own org's comps,
// without going through the founder-only Admin panel. Groups by stage ->
// division/round via the SAME groupMatchesByStage helper the Setup step's
// MatchScheduleBoard (TournamentSetup.tsx) uses — same two reads (GET
// /matches, GET .../stages), same grouping/labels, same isBracketStage
// honesty note. The one deliberate difference: this board reads
// MOST-RECENTLY-PLAYED-FIRST (recentFirst: true) so an admin scoring last
// night's games sees them at the top — Setup's board stays chronological
// (its existing order is untouched).
//
// Deliberately NOT reusing MatchScheduleBoard/MatchRow verbatim: those bundle
// inline score entry (ResultEntryForm) and a "Create match" action, which
// belong to Setup's admin surface. This page only links out to the match
// card — scoring/override/confirm already lives there and is already
// org-admin-reachable. Rows below are plain clickable links, same idiom the
// pre-board version of this page used.
// ─────────────────────────────────────────────────────────────────────────────

function formatMatchDate(ts?: number) {
  if (!ts) return null;
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ', ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function Matches() {
  const { activeComp, activeCompId, isLoading: compsLoading } = useWorkspace();

  const { data: matches, isLoading: matchesLoading } = useQuery({
    queryKey: ['matches', activeCompId],
    queryFn: () => api.matches(activeCompId!),
    enabled: !!activeCompId,
  });

  const { data: stages, isLoading: stagesLoading } = useQuery({
    queryKey: ['compStages', activeCompId],
    queryFn: () => api.listCompStages(activeCompId!),
    enabled: !!activeCompId,
  });

  if (compsLoading) return <PageSpinner />;

  if (!activeCompId) {
    return (
      <EmptyState
        title="No comp selected"
        message="Pick a competition from the sidebar to browse matches."
        icon={<Swords className="h-12 w-12" strokeWidth={1.5} />}
      />
    );
  }

  const isLoading = matchesLoading || stagesLoading;
  const allMatches = matches ?? [];
  const groups = isLoading ? [] : groupMatchesByStage(allMatches, stages ?? [], { recentFirst: true });
  // DIVISIONS carousel (below) needs the full CompStage row (config.gamesPerWeek,
  // plannedStartAt/startedAt) — StageMatchGroup only carries stageId/stageType/
  // stageStatus, so look the matching stage back up by id.
  const stageById = new Map((stages ?? []).map(s => [s.stageId, s]));

  return (
    <div>
      <div className={cn('mb-8 flex items-end justify-between gap-4', SETTLE)} style={settleDelay(0)}>
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
            Match Summary
          </p>
          <h1 className="mt-1 truncate font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            {activeComp?.name || activeCompId.replace('COMP#', '')}
          </h1>
        </div>
        {activeComp?.status && <StatusBadge status={activeComp.status} />}
      </div>

      {isLoading && <PageSpinner />}

      {!isLoading && allMatches.length === 0 && (
        <EmptyState
          title="Nothing forged yet"
          message="No matches recorded in this comp. Come back after match night."
          icon={<Trophy className="h-12 w-12" strokeWidth={1.5} />}
        />
      )}

      {!isLoading && allMatches.length > 0 && (
        <div className="space-y-3">
          {groups.map((g, i) => (
            <StageSection key={g.key} group={g} index={i} stage={g.stageId ? stageById.get(g.stageId) : undefined} compId={activeCompId} />
          ))}
        </div>
      )}
    </div>
  );
}

function StageSection({ group, index, stage, compId }: { group: StageMatchGroup; index: number; stage?: CompStage; compId: string | null }) {
  const title = group.stageType ? (STAGE_TYPE_LABELS[group.stageType] ?? group.stageType) : 'Legacy / unassigned';
  const totalCount = group.matches.length;
  const hint = group.stageId
    ? undefined
    : 'Matches with no stage link — created before this competition had a stage pipeline, or whose stage was since removed.';
  // Redesign (Lisa, 2026-07-19, from a mockup): a DIVISIONS stage's fixtures
  // no longer render as the flat week-merged `group.rounds` list — that
  // merges every division together, which Lisa found not obvious. Instead
  // they get their own per-division week-pager carousel (see
  // DivisionFixturesCarousel.tsx). Every OTHER stage type keeps the original
  // `group.rounds` rendering untouched. `stage` should always resolve for a
  // DIVISIONS group (it's looked up by the SAME stageId groupMatchesByStage
  // used to build this group), but fall back to the plain list if it somehow
  // doesn't rather than silently rendering nothing.
  const useDivisionCarousel = group.stageType === 'DIVISIONS' && !!stage && !!compId;

  return (
    <div className={cn(SETTLE)} style={settleDelay(index + 1)}>
      <CollapsibleSection
        title={title}
        hint={hint}
        right={group.stageStatus ? <StatusBadge status={group.stageStatus} /> : undefined}
        defaultOpen={totalCount > 0}
      >
        {group.isBracketStage && (
          <p className="text-xs text-ink-meta">
            Single/double-elimination play generates bracket slots, not match rows — bracket play lives in
            Setup → Competition (Elimination Bracket panel), not this board.
          </p>
        )}

        {!group.isBracketStage && totalCount === 0 && (
          <p className="text-xs text-ink-meta">No matches recorded yet in this stage.</p>
        )}

        {!group.isBracketStage && totalCount > 0 && (
          useDivisionCarousel ? (
            <DivisionFixturesCarousel stage={stage!} matches={group.matches} compId={compId!} />
          ) : (
            <div className="space-y-3">
              {group.rounds.map(round => (
                <div key={round.key}>
                  {round.label && (
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-meta">
                      {round.label}
                    </p>
                  )}
                  <div className="space-y-2">
                    {round.matches.map(m => <MatchLinkRow key={m.sk} m={m} />)}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </CollapsibleSection>
    </div>
  );
}

function MatchLinkRow({ m }: { m: MatchSummary }) {
  const isCompleted = m.status === 'CONFIRMED' && m.nightWinner;
  const dateStr = formatMatchDate(m.scheduledAt ?? m.createdAt);
  const clan1Label = clanLabelFrom(m.clan1DisplayName, m.clan1);
  const clan2Label = clanLabelFrom(m.clan2DisplayName, m.clan2);
  const winnerLabel = m.nightWinner
    ? (m.nightWinner === m.clan1 ? clan1Label : m.nightWinner === m.clan2 ? clan2Label : clanLabelFrom(undefined, m.nightWinner))
    : null;

  return (
    // Mobile: stack (flex-col) so Teams gets the full row width instead of
    // fighting the grid's phantom column-gaps that stay reserved even when
    // Date/Winner collapse to nothing (display:none removes a grid item but
    // NOT its track's gap) — that's what crushed team names here too. sm:+
    // reverts to the exact original 5-column grid (same template, same gap).
    <Link
      to={`/match/${encodeURIComponent(m.sk)}?compId=${encodeURIComponent(m.pk)}`}
      className={cn('plate-sm group flex flex-col gap-2 px-5 py-4 sm:grid sm:grid-cols-[1fr_auto_auto_auto_auto] sm:items-center sm:gap-4', MORPH)}
    >
      {/* Teams — left-aligned. Mobile-only flex-1 + min-width floor so a name
          never crushes past readability; sm:+ reverts to natural width + truncate,
          byte-for-byte the original behavior. */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Division tag — only DIVISIONS-stage matches carry groupIndex.
            Week-grouping (see groupMatchesByStage.ts) merges every division's
            fixtures into one "Week N" block, so this is what keeps a match's
            own division readable at a glance. */}
        {typeof m.groupIndex === 'number' && (
          <span className="shrink-0 font-mono text-[9px] font-bold uppercase tracking-widest text-ink-meta">
            Div {divisionLetter(m.groupIndex)}
          </span>
        )}
        <p className="min-w-[60px] flex-1 truncate font-display text-sm font-black uppercase tracking-wider text-white sm:min-w-0 sm:flex-none">
          {clan1Label}
        </p>
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-ink-meta shrink-0">
          vs
        </span>
        <p className="min-w-[60px] flex-1 truncate font-display text-sm font-black uppercase tracking-wider text-white sm:min-w-0 sm:flex-none">
          {clan2Label}
        </p>
      </div>

      {/* Date/time — middle (unchanged: sm:+ only, same as before) */}
      {dateStr && (
        <span className="hidden font-mono text-[10px] text-ink-meta sm:block">
          {dateStr}
        </span>
      )}

      {/* Winner (unchanged: sm:+ only, same as before) */}
      {winnerLabel ? (
        <span className="hidden items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-accent-500 sm:flex">
          <Trophy className="h-3 w-3" strokeWidth={3} />
          {winnerLabel}
        </span>
      ) : <span className="hidden sm:block" />}

      {/* Status + chevron — second line on mobile; sm:contents un-wraps them
          back into the grid as two independent columns (4th + 5th), so sm:+
          renders identically to the original 5-column grid. */}
      <div className="flex items-center gap-2 sm:contents">
        {isCompleted ? (
          <StatusBadge status="COMPLETED" />
        ) : (
          <StatusBadge status={m.status} />
        )}
        <ChevronRight className="h-4 w-4 text-ink-meta transition-colors group-hover:text-ink-secondary" />
      </div>
    </Link>
  );
}
